/**
 * E013-T011: WorkspaceDatabasesPanel
 *
 * Sidebar panel showing all databases across the workspace.
 * Enables Move and Mirror actions for databases in other docs.
 *
 * Features:
 * - Grouped by Doc with current doc highlighted
 * - Search/filter functionality
 * - Move: Move database from other doc to current doc
 * - Mirror: Create read-only mirror in current doc
 * - Navigate: Go to database in its source doc
 */

import { toast, Tooltip, useConfirmModal } from '@affine/component';
import { useGuard } from '@affine/core/components/guard';
import { useI18n } from '@affine/i18n';
import type { GfxController } from '@blocksuite/affine/std/gfx';
import {
  createDatabaseBlockDataSource,
  resolveDatabaseStorageModel,
} from '@blocksuite/affine-block-database';
import { parseXYWH, serializeXYWH } from '@blocksuite/global/gfx';
import {
  ArrowRightBigIcon,
  DatabaseTableViewIcon,
  DeleteIcon,
  MoveToIcon,
  PageIcon,
  ResetIcon,
  ToggleDownIcon,
  ToggleRightIcon,
} from '@blocksuite/icons/rc';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SidebarExpandableGroup,
  SidebarExpandableSection,
} from '../../../components/sidebar-expandable-section';
import { AnimatedNavigationPanelTreeChildren } from '../../../desktop/components/navigation-panel/tree/animated-children';
import { GraphQLService } from '../../cloud';
import { DocService, DocsService } from '../../doc';
import { EditorService } from '../../editor';
import { WorkbenchService } from '../../workbench/services/workbench';
import { WorkspaceService } from '../../workspace';
import { postDatabaseSystemDocAccessBindings } from '../file-import';
import { useWorkspaceCustomApps } from '../hooks/use-workspace-custom-apps';
import {
  filterDocGroupsForHiddenMailFolderDocIds,
  useHiddenMailFolderDocIds,
} from '../mail/mail-folder-visibility-filter';
import type { AutomationConfig } from '../services/automation';
import { confirmDatabaseDelete } from '../services/database-delete-confirm';
import {
  DatabaseDiscoveryService,
  type DatabaseInfo,
} from '../services/database-discovery';
import { permanentlyDeleteDatabase } from '../services/database-permanent-delete';
import { cinematicNavigateTo } from '../utils/cinematic-viewport';
import { withMovedCanvasBlockXYWH } from './canvas-block-move';
import {
  describeManagedDatabaseOwner,
  isManagedDatabaseProtected,
  ManagedDatabaseDeleteDialog,
  resolveCustomAppDatabaseOwner,
} from './components/managed-database-delete-dialog';
import {
  createDatabaseMoveAccessBinding,
  createDatabaseMovedEventDetail,
  type DatabaseColumn,
  getCanonicalDatabaseId,
  updateIncomingRelationColumnsAfterDatabaseMove,
  updateOutgoingRelationColumnsAfterDatabaseMove,
} from './database-move';
import { computeDatabaseReferenceMoveUpdates } from './database-reference-reconciliation';
import * as styles from './workspace-databases-panel.css';
import {
  getWorkspaceDatabaseCanvasTarget,
  getWorkspaceDatabaseDisplayTitle,
  getWorkspaceDatabaseNavigationErrorMessage,
} from './workspace-databases-panel-utils';

// =============================================================================
// Types
// =============================================================================

interface DocGroup {
  docId: string;
  docTitle: string;
  databases: DatabaseInfo[];
  isCurrent: boolean;
}

type PendingManagedDatabaseDelete = {
  action: 'trash' | 'permanent-delete';
  database: DatabaseInfo;
  displayTitle: string;
  docTitle: string;
};

/**
 * E018-S008-T006: Updates all relation columns affected by a database move.
 *
 * Two cases:
 * 1. INCOMING: Other databases have relations pointing TO the moved database
 *    → Update their targetDocId to the new location
 * 2. OUTGOING: The moved database has relations pointing to databases in the OLD doc
 *    → Update those to have explicit targetDocId (was same-doc, now cross-doc)
 */
async function updateRelationsAfterMove(
  docsService: DocsService,
  movedDatabaseId: string,
  newDocId: string,
  oldDocId?: string
): Promise<void> {
  const bridge = window.__databaseBridge;
  if (!bridge?.findIncomingRelations) {
    throw new Error(
      'Cannot move database: database discovery bridge is not ready for relation reconciliation.'
    );
  }

  // Find all databases with relations pointing to the moved database.
  const incomingDbs = bridge.findIncomingRelations(movedDatabaseId) as Array<{
    blockId: string;
    docId: string;
    columns: DatabaseColumn[];
  }>;

  if (incomingDbs.length === 0) {
    console.log('[WorkspaceDatabases] No incoming relations to update');
  } else {
    console.log(
      '[WorkspaceDatabases] Updating relations in',
      incomingDbs.length,
      'databases'
    );
  }

  for (const sourceDb of incomingDbs) {
    const relationColumnIds = (sourceDb.columns || [])
      .filter(
        col =>
          col.type === 'relation' &&
          (col as { targetDatabaseId?: string }).targetDatabaseId ===
            movedDatabaseId
      )
      .map(col => col.id);

    if (relationColumnIds.length === 0) continue;

    console.log('[WorkspaceDatabases] Found relation columns to update:', {
      dbId: sourceDb.blockId,
      columnIds: relationColumnIds,
    });

    try {
      const { doc: docRecord, release } = docsService.open(sourceDb.docId);
      try {
        const blockSuiteDoc = docRecord.blockSuiteDoc;

        if (!blockSuiteDoc) {
          console.warn(
            '[WorkspaceDatabases] Doc has no blockSuiteDoc:',
            sourceDb.docId
          );
          continue;
        }

        const dbBlock = blockSuiteDoc.getBlock(sourceDb.blockId);
        if (!dbBlock) {
          console.warn(
            '[WorkspaceDatabases] Database block not found:',
            sourceDb.blockId
          );
          continue;
        }

        const dataSource = createDatabaseBlockDataSource(dbBlock.model as any);
        const storageModel = dataSource.storageModel as any;
        const storageDoc = storageModel.store as {
          transact: (cb: () => void) => void;
        };
        const columns = storageModel.props.columns as
          | DatabaseColumn[]
          | undefined;
        if (!Array.isArray(columns)) {
          console.warn('[WorkspaceDatabases] No columns found in database');
          continue;
        }

        const update = updateIncomingRelationColumnsAfterDatabaseMove(
          columns,
          movedDatabaseId,
          newDocId
        );
        if (update.updatedColumnIds.length > 0) {
          storageDoc.transact(() => {
            storageModel.props.columns = update.columns;
          });
          console.log(
            '[WorkspaceDatabases] Updated incoming relation columns',
            {
              dbId: sourceDb.blockId,
              columnIds: update.updatedColumnIds,
              movedDatabaseId,
              newTargetDocId: newDocId,
            }
          );
        }
      } finally {
        release();
      }
    } catch (e) {
      console.error(
        '[WorkspaceDatabases] Failed to update relation in:',
        sourceDb.docId,
        e
      );
      throw e;
    }
  }

  // PART 2: Update OUTGOING relations from the moved database.
  // Same-doc relations to other old-doc databases must become explicit
  // cross-doc relations. Self-relations keep pointing at the moved database.
  if (oldDocId) {
    try {
      const { doc: movedDocRecord, release: releaseMovedDoc } =
        docsService.open(newDocId);
      try {
        const movedBlockSuiteDoc = movedDocRecord.blockSuiteDoc;
        const movedDbBlock = movedBlockSuiteDoc?.getBlock(movedDatabaseId);

        if (!movedBlockSuiteDoc || !movedDbBlock) {
          throw new Error(
            `Moved database "${movedDatabaseId}" was not found in doc "${newDocId}".`
          );
        }

        const dataSource = createDatabaseBlockDataSource(
          movedDbBlock.model as any
        );
        const storageModel = dataSource.storageModel as any;
        const storageDoc = storageModel.store as {
          transact: (cb: () => void) => void;
        };
        const movedColumns = storageModel.props.columns as
          | DatabaseColumn[]
          | undefined;

        if (!Array.isArray(movedColumns)) {
          return;
        }

        const update = updateOutgoingRelationColumnsAfterDatabaseMove(
          movedColumns,
          movedDatabaseId,
          oldDocId
        );
        if (update.updatedColumnIds.length > 0) {
          storageDoc.transact(() => {
            storageModel.props.columns = update.columns;
          });
          console.log(
            '[WorkspaceDatabases] Updated outgoing relation columns',
            {
              dbId: movedDatabaseId,
              columnIds: update.updatedColumnIds,
              targetDocId: oldDocId,
            }
          );
        }
      } finally {
        releaseMovedDoc();
      }
    } catch (e) {
      console.error(
        '[WorkspaceDatabases] Failed to update outgoing relations:',
        e
      );
      throw e;
    }
  }
}

async function updateAutomationsAfterDatabaseMove(
  docsService: DocsService,
  movedDatabaseId: string,
  newDocId: string
): Promise<void> {
  const bridge = window.__databaseBridge;
  if (!bridge?.findAutomationsReferencingDatabase) {
    throw new Error(
      'Cannot move database: database discovery bridge is not ready for automation reconciliation.'
    );
  }

  const automations =
    bridge.findAutomationsReferencingDatabase(movedDatabaseId);
  if (automations.length === 0) {
    return;
  }

  for (const automation of automations) {
    try {
      const { doc: docRecord, release } = docsService.open(automation.docId);
      try {
        const blockSuiteDoc = docRecord.blockSuiteDoc;
        const automationBlock = blockSuiteDoc?.getBlock(automation.blockId);
        const props = automationBlock?.model?.props as
          | (Partial<AutomationConfig> & Record<string, unknown>)
          | undefined;
        if (!blockSuiteDoc || !automationBlock || !props) {
          throw new Error(
            `Automation "${automation.blockId}" was not found in doc "${automation.docId}".`
          );
        }

        const updates = computeDatabaseReferenceMoveUpdates(
          props as AutomationConfig,
          [{ blockId: movedDatabaseId, docId: newDocId }],
          automation.docId
        );
        if (!updates) {
          continue;
        }

        blockSuiteDoc.transact(() => {
          for (const [key, value] of Object.entries(updates)) {
            props[key] = value;
          }
        });
        console.log('[WorkspaceDatabases] Updated automation database refs', {
          automationId: automation.blockId,
          docId: automation.docId,
          movedDatabaseId,
          updates,
        });
      } finally {
        release();
      }
    } catch (e) {
      console.error(
        '[WorkspaceDatabases] Failed to update automation refs:',
        automation,
        e
      );
      throw e;
    }
  }
}

// =============================================================================
// Component
// =============================================================================

export function WorkspaceDatabasesPanel() {
  const t = useI18n();
  const docService = useServiceOptional(DocService);
  const docsService = useService(DocsService);
  const editorService = useServiceOptional(EditorService);
  const workspace = useService(WorkspaceService).workspace;
  const workbenchService = useService(WorkbenchService);
  const discoveryService = useService(DatabaseDiscoveryService);
  const graphqlService = useService(GraphQLService);
  const canViewCustomApps = useGuard('Workspace_CustomApps_View') === true;
  const { views: customApps } = useWorkspaceCustomApps(
    canViewCustomApps ? workspace.id : null
  );

  // E018-S010: Use canvas-only databases (In-Note DBs filtered out)
  const databasesByDoc = useLiveData(
    discoveryService.userFacingCanvasDatabasesByDoc$
  );
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();
  const visibleDatabasesByDoc = useMemo(
    () =>
      filterDocGroupsForHiddenMailFolderDocIds(
        databasesByDoc,
        hiddenMailFolderDocIds
      ),
    [databasesByDoc, hiddenMailFolderDocIds]
  );
  const currentDocId = docService?.doc.id;
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);
  const [isMoving, setIsMoving] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [pendingManagedDelete, setPendingManagedDelete] =
    useState<PendingManagedDatabaseDelete | null>(null);
  // E018-S016: Workspace-wide trashcan (RBAC-filtered)
  const trashcanByDoc = useLiveData(
    discoveryService.userFacingAccessibleTrashcanDatabasesByDoc$
  );
  const visibleTrashcanByDoc = useMemo(
    () =>
      filterDocGroupsForHiddenMailFolderDocIds(
        trashcanByDoc,
        hiddenMailFolderDocIds
      ),
    [hiddenMailFolderDocIds, trashcanByDoc]
  );
  const [deletedExpanded, setDeletedExpanded] = useState(false); // E018: Collapsed by default

  // Expand trash section when ghost node dispatches 'ghost-open-trashcan' event (database type)
  // Only react to _routed events (re-dispatched by StreamLainSidebarSetup after panel mount)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (
        detail?._routed &&
        (!detail.ghostType || detail.ghostType === 'database')
      ) {
        setDeletedExpanded(true);
      }
    };
    window.addEventListener('ghost-open-trashcan', handler);
    return () => window.removeEventListener('ghost-open-trashcan', handler);
  }, []);

  // E018-S016: Total count of trashed databases
  const totalTrashedCount = useMemo(
    () =>
      visibleTrashcanByDoc.reduce(
        (sum, group) => sum + group.databases.length,
        0
      ),
    [visibleTrashcanByDoc]
  );

  const confirmModal = useConfirmModal();

  const resolveManagedOwner = useCallback(
    (database: DatabaseInfo, docTitle: string) =>
      describeManagedDatabaseOwner({
        managedBy: database.managedBy,
        fallbackDocTitle: docTitle,
        explicitOwner: resolveCustomAppDatabaseOwner({
          blockId: database.blockId,
          docId: database.docId,
          customApps,
          openCustomApp: customAppId =>
            workbenchService.workbench.open(`/custom-apps/${customAppId}`, {
              at: 'active',
            }),
        }),
        openSourceDoc: () =>
          workbenchService.workbench.open(`/${database.docId}`),
      }),
    [customApps, workbenchService]
  );

  const resolveCustomAppOwner = useCallback(
    (database: DatabaseInfo) =>
      resolveCustomAppDatabaseOwner({
        blockId: database.blockId,
        docId: database.docId,
        customApps,
        openCustomApp: customAppId =>
          workbenchService.workbench.open(`/custom-apps/${customAppId}`, {
            at: 'active',
          }),
      }),
    [customApps, workbenchService]
  );

  // E018-S016: Restore from trashcan (current doc only for now)
  // Cross-doc restore requires navigating to that doc first
  const handleRestore = useCallback(
    async (item: DatabaseInfo) => {
      const isCurrentDoc = item.docId === currentDocId;

      if (!isCurrentDoc) {
        // For cross-doc: navigate to the doc and let user restore there
        console.log(
          '[WorkspaceDatabases] Navigate to doc for restore:',
          item.docId
        );
        workbenchService.workbench.open(`/${item.docId}`);
        return;
      }

      try {
        const { restoreFromTrashcan } =
          await import('@blocksuite/affine-block-streamlain');

        const doc = editorContainer?.doc;
        if (!doc) return;

        const edgelessRoot = document.querySelector('affine-edgeless-root');
        const gfx = (edgelessRoot as { gfx?: GfxController } | null)?.gfx;

        if (gfx) {
          await restoreFromTrashcan(doc, gfx, item.blockId);
          discoveryService.refresh();
        }
      } catch (e) {
        console.error('[WorkspaceDatabases] Failed to restore database:', e);
      }
    },
    [currentDocId, editorContainer, workbenchService, discoveryService]
  );

  // E018-S016: Permanent delete from trashcan (supports cross-doc). The
  // shared confirm lists relation dependencies; confirmed deletes clean up
  // dependent relation columns, drop the server-side system documents and
  // finally remove the trashcan item.
  const deleteDatabasePermanently = useCallback(
    async (item: DatabaseInfo, docTitle: string, displayTitle: string) => {
      try {
        const dependencies = await discoveryService.resolveDeleteDependencies(
          item.blockId,
          item.docId
        );
        const confirmed = await confirmDatabaseDelete(confirmModal, {
          action: 'permanent-delete',
          databaseTitle: displayTitle,
          docTitle,
          dependencies,
        });
        if (!confirmed) return;

        const outcome = await permanentlyDeleteDatabase(
          {
            workspace,
            graphql: graphqlService,
            discovery: discoveryService,
          },
          item
        );
        if (outcome !== 'deleted') {
          console.error(
            '[WorkspaceDatabases] Permanent delete did not complete:',
            outcome
          );
        }
      } catch (e) {
        console.error('[WorkspaceDatabases] Failed to delete database:', e);
      }
    },
    [confirmModal, discoveryService, graphqlService, workspace]
  );

  const handlePermanentDelete = useCallback(
    async (item: DatabaseInfo, docTitle: string, displayTitle: string) => {
      if (isManagedDatabaseProtected(item)) {
        setPendingManagedDelete({
          action: 'permanent-delete',
          database: item,
          displayTitle,
          docTitle,
        });
        return;
      }
      await deleteDatabasePermanently(item, docTitle, displayTitle);
    },
    [deleteDatabasePermanently]
  );

  // E018-S010-T003: Move active database to trashcan (current doc only). The
  // trash guard registered by the discovery bridge shows the dependency
  // confirm before the block moves.
  const moveDatabaseToTrash = useCallback(
    async (database: DatabaseInfo) => {
      const doc = editorContainer?.doc;
      if (!doc) return;

      try {
        const { requestMoveToTrashcan } =
          await import('@blocksuite/affine-block-streamlain');
        const outcome = await requestMoveToTrashcan(doc, database.blockId, {
          allowProtected: true,
        });
        if (outcome === 'moved') {
          discoveryService.refresh();
        } else if (outcome === 'rejected') {
          console.error(
            '[WorkspaceDatabases] Database could not be moved to trash:',
            database.blockId
          );
        }
      } catch (e) {
        console.error('[WorkspaceDatabases] Failed to move to trash:', e);
      }
    },
    [editorContainer, discoveryService]
  );

  const handleMoveToTrash = useCallback(
    async (database: DatabaseInfo, docTitle: string, displayTitle: string) => {
      if (isManagedDatabaseProtected(database)) {
        setPendingManagedDelete({
          action: 'trash',
          database,
          displayTitle,
          docTitle,
        });
        return;
      }
      await moveDatabaseToTrash(database);
    },
    [moveDatabaseToTrash]
  );

  const confirmManagedDelete = useCallback(async () => {
    if (!pendingManagedDelete) return;
    const { action, database, docTitle, displayTitle } = pendingManagedDelete;
    setPendingManagedDelete(null);
    if (action === 'trash') {
      await moveDatabaseToTrash(database);
    } else {
      await deleteDatabasePermanently(database, docTitle, displayTitle);
    }
  }, [deleteDatabasePermanently, moveDatabaseToTrash, pendingManagedDelete]);

  // Group databases by doc with current doc first
  const docGroups = useMemo((): DocGroup[] => {
    const groups: DocGroup[] = visibleDatabasesByDoc.map(group => ({
      docId: group.docId,
      docTitle: group.docTitle,
      databases: group.databases,
      isCurrent: group.docId === currentDocId,
    }));
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();

    // Filter by search query
    const filtered = normalizedSearchQuery
      ? groups
          .map(group => ({
            ...group,
            databases: group.databases.filter(db => {
              const displayTitle = getWorkspaceDatabaseDisplayTitle(
                db,
                group.databases
              );
              return (
                displayTitle.toLowerCase().includes(normalizedSearchQuery) ||
                db.databaseTitle
                  .toLowerCase()
                  .includes(normalizedSearchQuery) ||
                db.docTitle.toLowerCase().includes(normalizedSearchQuery) ||
                db.blockId.toLowerCase().includes(normalizedSearchQuery)
              );
            }),
          }))
          .filter(group => group.databases.length > 0)
      : groups;

    // Sort: current doc first, then alphabetically
    return filtered.sort((a, b) => {
      if (a.isCurrent && !b.isCurrent) return -1;
      if (!a.isCurrent && b.isCurrent) return 1;
      return a.docTitle.localeCompare(b.docTitle);
    });
  }, [visibleDatabasesByDoc, currentDocId, searchQuery]);

  // Toggle doc section expansion
  const toggleDocExpanded = useCallback((docId: string, isCurrent: boolean) => {
    setExpandedDocs(prev => {
      const next = new Set(prev);
      if (isCurrent) {
        const collapsedKey = `collapsed:${docId}`;
        if (next.has(collapsedKey)) {
          next.delete(collapsedKey);
        } else {
          next.delete(docId);
          next.add(collapsedKey);
        }
        return next;
      }
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  }, []);

  // Check if doc is expanded (current doc expanded by default)
  const isExpanded = useCallback(
    (docId: string, isCurrent: boolean) => {
      if (expandedDocs.has(docId)) return true;
      // Current doc is expanded by default unless explicitly collapsed
      return isCurrent && !expandedDocs.has(`collapsed:${docId}`);
    },
    [expandedDocs]
  );

  // Navigate to database in its source doc (other docs)
  const handleNavigate = useCallback(
    (database: DatabaseInfo) => {
      console.log('[WorkspaceDatabases] Navigating to:', database.docId);
      // Navigate to the doc containing this database
      workbenchService.workbench.open(`/${database.docId}`);
    },
    [workbenchService]
  );

  // E018-S010-T004: Cinematic navigation for current doc databases
  const handleCinematicNavigate = useCallback((database: DatabaseInfo) => {
    const target = getWorkspaceDatabaseCanvasTarget(database);
    const reportNavigationError = (error?: unknown) => {
      console.error('[WorkspaceDatabases] Failed to navigate:', {
        blockId: database.blockId,
        docId: database.docId,
        error,
      });
      const message = getWorkspaceDatabaseNavigationErrorMessage(database);
      toast(message);
    };

    if (!target) {
      reportNavigationError('missing-target-position');
      return;
    }

    const edgelessRoot = document.querySelector('affine-edgeless-root');
    const gfx = (edgelessRoot as { gfx?: { viewport?: unknown } } | null)?.gfx;
    if (!gfx?.viewport) {
      reportNavigationError('missing-edgeless-viewport');
      return;
    }

    cinematicNavigateTo(
      gfx.viewport as {
        centerX: number;
        centerY: number;
        zoom: number;
        setCenter: (x: number, y: number) => void;
        setZoom: (zoom: number, focusPoint?: { x: number; y: number }) => void;
      },
      target.centerX,
      target.centerY,
      { duration: 700, targetZoom: 1.0 }
    ).catch(reportNavigationError);
  }, []);

  // E018-S008-T006: Move database to current doc
  // Uses BlockSuite Transformer for proper block insertion with xywh positioning
  const handleMove = useCallback(
    async (database: DatabaseInfo) => {
      if (!currentDocId || database.docId === currentDocId || isMoving) return;

      const customAppOwner = resolveCustomAppOwner(database);
      if (customAppOwner || isManagedDatabaseProtected(database)) {
        toast(
          t['com.streamlain.streamlain.workspace.databaseMoveManagedBlocked']({
            owner:
              customAppOwner?.label ??
              database.managedBy ??
              t[
                'com.streamlain.streamlain.workspace.databaseMoveManagedOwner'
              ](),
          })
        );
        return;
      }

      const targetDoc = editorContainer?.doc;
      if (!targetDoc) {
        console.error('[WorkspaceDatabases] No target doc available');
        return;
      }

      // Get gfx controller for viewport positioning
      const edgelessRoot = document.querySelector('affine-edgeless-root');
      const gfx = (
        edgelessRoot as {
          gfx?: { viewport?: { centerX: number; centerY: number } };
        } | null
      )?.gfx;
      if (!gfx?.viewport) {
        console.error(
          '[WorkspaceDatabases] No gfx/viewport - are you in Edgeless mode?'
        );
        return;
      }

      setIsMoving(true);
      console.log('[WorkspaceDatabases] Starting move:', {
        from: database.docId,
        to: currentDocId,
        blockId: database.blockId,
        viewportCenter: { x: gfx.viewport.centerX, y: gfx.viewport.centerY },
      });

      let insertedTargetBlock = false;
      let registeredTargetAccessBinding = false;
      let sourceDeleted = false;
      let oldAccessBinding: ReturnType<
        typeof createDatabaseMoveAccessBinding
      > | null = null;

      try {
        const { doc: sourceDocRecord, release: releaseSource } =
          docsService.open(database.docId);
        try {
          const sourceBlockSuiteDoc = sourceDocRecord.blockSuiteDoc;

          if (!sourceBlockSuiteDoc) {
            throw new Error('Source doc has no blockSuiteDoc');
          }

          if (targetDoc.getBlock(database.blockId)) {
            throw new Error(
              `Target doc already contains database block "${database.blockId}".`
            );
          }

          const dbBlock = sourceBlockSuiteDoc.getBlock(database.blockId);
          if (!dbBlock) {
            throw new Error(`Database block "${database.blockId}" not found.`);
          }

          const databaseId = getCanonicalDatabaseId(
            dbBlock.model as {
              id: string;
              props: Record<string, unknown>;
            }
          );
          if (databaseId !== database.blockId) {
            throw new Error(
              `Database move requires stable block/database id; block="${database.blockId}", databaseId="${databaseId}".`
            );
          }

          const sourceStorageModel = resolveDatabaseStorageModel(
            dbBlock.model as any
          );
          oldAccessBinding = createDatabaseMoveAccessBinding({
            workspaceId: workspace.id,
            accessDocId: database.docId,
            model: dbBlock.model as {
              id: string;
              props: Record<string, unknown>;
            },
            storageModel: sourceStorageModel as {
              props?: Record<string, unknown>;
            } | null,
          });

          const sourceProps = dbBlock.model.props as { xywh?: string };
          const sourceXywh = sourceProps.xywh;
          let width = 400;
          let height = 300;
          const parsedSourceXYWH = parseXYWH(sourceXywh);
          if (parsedSourceXYWH) {
            [, , width, height] = parsedSourceXYWH;
          }

          const centerX = gfx.viewport.centerX;
          const centerY = gfx.viewport.centerY;
          const newXywh = serializeXYWH(
            centerX - width / 2,
            centerY - height / 2,
            width,
            height
          );

          console.log('[WorkspaceDatabases] Calculated position:', {
            centerX,
            centerY,
            width,
            height,
            newXywh,
          });

          const transformer = targetDoc.getTransformer();
          const blockSnapshot = transformer.blockToSnapshot(dbBlock.model);
          if (!blockSnapshot) {
            throw new Error('Failed to create BlockSnapshot');
          }

          console.log('[WorkspaceDatabases] BlockSnapshot created:', {
            id: blockSnapshot.id,
            flavour: blockSnapshot.flavour,
            childrenCount: blockSnapshot.children?.length ?? 0,
          });

          const movedBlockSnapshot = withMovedCanvasBlockXYWH(
            blockSnapshot,
            newXywh
          );
          const restoredModel = await transformer.snapshotToBlock(
            movedBlockSnapshot,
            targetDoc,
            targetDoc.root?.id
          );

          if (!restoredModel) {
            throw new Error('snapshotToBlock failed');
          }

          if (restoredModel.id !== database.blockId) {
            targetDoc.deleteBlock(restoredModel);
            throw new Error(
              `Database move changed block id from "${database.blockId}" to "${restoredModel.id}".`
            );
          }

          insertedTargetBlock = true;
          console.log('[WorkspaceDatabases] Block inserted:', restoredModel.id);

          targetDoc.updateBlock(database.blockId, { xywh: newXywh });
          console.log('[WorkspaceDatabases] Position updated');

          const targetAccessBinding = createDatabaseMoveAccessBinding({
            workspaceId: workspace.id,
            accessDocId: currentDocId,
            model: restoredModel as {
              id: string;
              props: Record<string, unknown>;
            },
            storageModel:
              (resolveDatabaseStorageModel(restoredModel as any) as {
                props?: Record<string, unknown>;
              } | null) ??
              (sourceStorageModel as {
                props?: Record<string, unknown>;
              } | null),
          });

          await postDatabaseSystemDocAccessBindings({
            workspaceId: workspace.id,
            bindings: [targetAccessBinding],
          });
          registeredTargetAccessBinding = true;

          await updateRelationsAfterMove(
            docsService,
            databaseId,
            currentDocId,
            database.docId
          );
          await updateAutomationsAfterDatabaseMove(
            docsService,
            databaseId,
            currentDocId
          );

          sourceBlockSuiteDoc.deleteBlock(dbBlock.model);
          sourceDeleted = true;
          console.log('[WorkspaceDatabases] Deleted from source');
        } catch (e) {
          if (!sourceDeleted && insertedTargetBlock) {
            const targetBlock = targetDoc.getBlock(database.blockId);
            if (targetBlock) {
              targetDoc.deleteBlock(targetBlock.model);
            }
            if (registeredTargetAccessBinding && oldAccessBinding) {
              await postDatabaseSystemDocAccessBindings({
                workspaceId: workspace.id,
                bindings: [oldAccessBinding],
              });
            }
          }
          throw e;
        } finally {
          releaseSource();
        }

        discoveryService.refresh();

        await new Promise<void>((resolve, reject) => {
          let resolved = false;
          let subscription: { unsubscribe: () => void } | null = null;

          const cleanup = () => {
            if (subscription) {
              subscription.unsubscribe();
            }
          };

          subscription = discoveryService.databases$.subscribe(dbs => {
            if (
              dbs.some(
                db =>
                  db.blockId === database.blockId && db.docId === currentDocId
              ) &&
              !resolved
            ) {
              resolved = true;
              setTimeout(() => {
                cleanup();
                resolve();
              }, 0);
            }
          });

          // Fail loud if the workspace registry does not observe the moved
          // database. Proceeding would leave relation targets half-migrated.
          setTimeout(() => {
            if (!resolved) {
              resolved = true;
              cleanup();
              reject(
                new Error(
                  `Moved database "${database.blockId}" was not observed by workspace discovery.`
                )
              );
            }
          }, 10_000);
        });

        window.dispatchEvent(
          new CustomEvent('streamlain:database-moved', {
            detail: createDatabaseMovedEventDetail({
              databaseId: database.blockId,
              oldDocId: database.docId,
              newDocId: currentDocId,
              oldBlockId: database.blockId,
              newBlockId: database.blockId,
            }),
          })
        );

        console.log('[WorkspaceDatabases] Move successful:', {
          blockId: database.blockId,
          docId: currentDocId,
        });
      } catch (e) {
        console.error('[WorkspaceDatabases] Move failed:', e);
      } finally {
        setIsMoving(false);
      }
    },
    [
      currentDocId,
      editorContainer,
      discoveryService,
      docsService,
      isMoving,
      resolveCustomAppOwner,
      t,
      workspace.id,
    ]
  );

  const totalDatabases = useMemo(
    () => docGroups.reduce((sum, g) => sum + g.databases.length, 0),
    [docGroups]
  );

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>
          {t['com.streamlain.streamlain.workspace.databases']()}
        </span>
        <span className={styles.dbCount}>{totalDatabases}</span>
      </div>

      {/* Search */}
      <div className={styles.searchContainer}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t[
            'com.streamlain.streamlain.workspace.search-databases'
          ]()}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Scrollable Content */}
      <div className={styles.scrollableContent}>
        {docGroups.length === 0 ? (
          <div className={styles.emptyState}>
            <DatabaseTableViewIcon
              width={32}
              height={32}
              className={styles.emptyIcon}
            />
            <span>
              {t['com.streamlain.streamlain.workspace.no-databases']()}
            </span>
          </div>
        ) : (
          docGroups.map(group => {
            const expanded = isExpanded(group.docId, group.isCurrent);

            return (
              <div key={group.docId} className={styles.docSection}>
                {/* Doc Header */}
                <div
                  className={
                    group.isCurrent ? styles.docHeaderCurrent : styles.docHeader
                  }
                  onClick={() =>
                    toggleDocExpanded(group.docId, group.isCurrent)
                  }
                >
                  {expanded ? (
                    <ToggleDownIcon
                      width={16}
                      height={16}
                      className={styles.docIcon}
                    />
                  ) : (
                    <ToggleRightIcon
                      width={16}
                      height={16}
                      className={styles.docIcon}
                    />
                  )}
                  <PageIcon width={16} height={16} className={styles.docIcon} />
                  <span className={styles.docTitle}>{group.docTitle}</span>
                  {group.isCurrent && (
                    <span className={styles.currentBadge}>
                      {t['com.streamlain.streamlain.common.current']()}
                    </span>
                  )}
                  <span className={styles.dbCount}>
                    {group.databases.length}
                  </span>
                </div>

                {/* Database List */}
                <AnimatedNavigationPanelTreeChildren>
                  {expanded ? (
                    <div
                      key={`${group.docId}:databases`}
                      className={styles.databaseList}
                    >
                      {group.databases.map(db => {
                        const displayTitle = getWorkspaceDatabaseDisplayTitle(
                          db,
                          group.databases
                        );
                        const customAppOwner = resolveCustomAppOwner(db);
                        const moveTooltip = customAppOwner
                          ? t[
                              'com.streamlain.streamlain.workspace.databaseMoveManagedBlocked'
                            ]({ owner: customAppOwner.label })
                          : t[
                              'com.streamlain.streamlain.common.move-to-current-doc'
                            ]();
                        return (
                          <div
                            key={db.blockId}
                            className={styles.databaseItem}
                            onClick={() =>
                              group.isCurrent
                                ? handleCinematicNavigate(db)
                                : handleNavigate(db)
                            }
                          >
                            <div className={styles.databaseIcon}>
                              <DatabaseTableViewIcon width={18} height={18} />
                            </div>
                            <div className={styles.databaseInfo}>
                              <div className={styles.databaseName}>
                                {displayTitle}
                              </div>
                              <div className={styles.databaseMeta}>
                                {t[
                                  'com.streamlain.streamlain.workspace.columns-count'
                                ]({ count: String(db.columns.length) })}
                              </div>
                            </div>

                            {/* Actions for current doc: Delete button */}
                            {group.isCurrent && (
                              <div className={styles.databaseActions}>
                                <Tooltip
                                  content={t[
                                    'com.streamlain.streamlain.common.move-to-trash'
                                  ]()}
                                >
                                  <button
                                    className={styles.moveButton}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleMoveToTrash(
                                        db,
                                        group.docTitle,
                                        displayTitle
                                      ).catch(console.error);
                                    }}
                                  >
                                    <DeleteIcon width={14} height={14} />
                                  </button>
                                </Tooltip>
                              </div>
                            )}

                            {/* Actions for other docs: Move + Navigate */}
                            {!group.isCurrent && (
                              <div className={styles.databaseActions}>
                                <Tooltip content={moveTooltip}>
                                  <button
                                    className={styles.moveButton}
                                    disabled={isMoving}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleMove(db).catch(console.error);
                                    }}
                                  >
                                    <MoveToIcon width={14} height={14} />
                                  </button>
                                </Tooltip>
                                <Tooltip
                                  content={t[
                                    'com.streamlain.streamlain.common.go-to-source'
                                  ]()}
                                >
                                  <button
                                    className={styles.navigateButton}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleNavigate(db);
                                    }}
                                  >
                                    <ArrowRightBigIcon width={14} height={14} />
                                  </button>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </AnimatedNavigationPanelTreeChildren>
              </div>
            );
          })
        )}
      </div>

      {/* E018-S016: Workspace-wide Deleted Section */}
      {totalTrashedCount > 0 && (
        <SidebarExpandableGroup placement="bottom">
          <SidebarExpandableSection
            expanded={deletedExpanded}
            onExpandedChange={setDeletedExpanded}
            placement="bottom"
            title={`${t['com.streamlain.streamlain.common.deleted']()} (${totalTrashedCount})`}
            contentClassName={styles.bottomSectionContent}
          >
            {visibleTrashcanByDoc.map(docGroup => {
              const isCurrent = docGroup.docId === currentDocId;
              return (
                <div key={docGroup.docId} className={styles.docSection}>
                  <div
                    className={
                      isCurrent ? styles.docHeaderCurrent : styles.docHeader
                    }
                  >
                    <PageIcon
                      width={14}
                      height={14}
                      className={styles.docIcon}
                    />
                    <span className={styles.docTitle}>{docGroup.docTitle}</span>
                    {isCurrent && (
                      <span className={styles.currentBadge}>
                        {t['com.streamlain.streamlain.common.current']()}
                      </span>
                    )}
                    <span className={styles.dbCount}>
                      {docGroup.databases.length}
                    </span>
                  </div>

                  <div className={styles.databaseList}>
                    {docGroup.databases.map(db => {
                      const displayTitle = getWorkspaceDatabaseDisplayTitle(
                        db,
                        docGroup.databases
                      );
                      return (
                        <div
                          key={db.blockId}
                          className={styles.databaseItem}
                          data-testid="database-trash-item"
                        >
                          <div className={styles.databaseIcon}>
                            <DatabaseTableViewIcon width={18} height={18} />
                          </div>
                          <div className={styles.databaseInfo}>
                            <div className={styles.databaseName}>
                              {displayTitle}
                            </div>
                            <div className={styles.databaseMeta}>
                              {t['com.streamlain.streamlain.common.deleted']()}
                            </div>
                          </div>

                          {/* Restore + Delete buttons */}
                          <div className={styles.databaseActions}>
                            <Tooltip
                              content={t[
                                'com.streamlain.streamlain.common.restore'
                              ]()}
                            >
                              <button
                                data-testid="database-trash-restore"
                                className={styles.navigateButton}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleRestore(db).catch(console.error);
                                }}
                              >
                                <ResetIcon width={14} height={14} />
                              </button>
                            </Tooltip>
                            <Tooltip
                              content={t[
                                'com.streamlain.streamlain.common.delete-permanently'
                              ]()}
                            >
                              <button
                                data-testid="database-trash-delete-permanently"
                                className={styles.moveButton}
                                onClick={e => {
                                  e.stopPropagation();
                                  handlePermanentDelete(
                                    db,
                                    docGroup.docTitle,
                                    displayTitle
                                  ).catch(console.error);
                                }}
                              >
                                <DeleteIcon width={14} height={14} />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </SidebarExpandableSection>
        </SidebarExpandableGroup>
      )}
      <ManagedDatabaseDeleteDialog
        open={pendingManagedDelete !== null}
        onOpenChange={open => {
          if (!open) setPendingManagedDelete(null);
        }}
        actionLabel={
          pendingManagedDelete?.action === 'permanent-delete'
            ? 'Endgültig löschen'
            : 'In Papierkorb legen'
        }
        databaseTitle={pendingManagedDelete?.displayTitle ?? ''}
        description={
          pendingManagedDelete?.action === 'permanent-delete'
            ? 'Diese systemverwaltete Database wird endgültig gelöscht.'
            : 'Diese systemverwaltete Database wird in den Papierkorb verschoben.'
        }
        owner={
          pendingManagedDelete
            ? resolveManagedOwner(
                pendingManagedDelete.database,
                pendingManagedDelete.docTitle
              )
            : null
        }
        onConfirm={confirmManagedDelete}
      />
    </div>
  );
}
